import React, { useMemo, useCallback } from 'react';
import { 
  ChevronUp, 
  ChevronDown, 
  MoreHorizontal,
  Calendar,
  User
} from 'lucide-react';
import { FileTypeIcon, getFileTypeCategory, FileTypeCategory } from './FileTypeIcon';
import { SPEDocument } from '../../services/sharePointEmbeddedService';

/**
 * Enhanced document interface with Microsoft Graph metadata
 */
interface EnhancedSPEDocument extends SPEDocument {
  fileExtension: string;
  formattedSize: string;
  fileTypeCategory: FileTypeCategory;
  canPreview: boolean;
  isOfficeDocument: boolean;
}

/**
 * Sort configuration for document list
 */
interface SortConfig {
  column: 'name' | 'size' | 'type' | 'created' | 'modified';
  direction: 'asc' | 'desc';
}

/**
 * Props for DocumentList component
 */
interface DocumentListProps {
  documents: SPEDocument[];
  isLoading?: boolean;
  onDocumentAction: (action: string, document: SPEDocument, event?: React.MouseEvent) => void;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  sortConfig?: SortConfig;
  className?: string;
}

/**
 * Format file size to human readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Format date to readable format
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) {
    return 'Today';
  } else if (diffDays === 2) {
    return 'Yesterday';
  } else if (diffDays <= 7) {
    return `${diffDays - 1} days ago`;
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  }
};

/**
 * Extract username from Microsoft Graph user info
 */
const getDisplayName = (userInfo?: { user?: { displayName?: string; email?: string } }): string => {
  if (userInfo?.user?.displayName) {
    return userInfo.user.displayName;
  }
  if (userInfo?.user?.email) {
    return userInfo.user.email.split('@')[0];
  }
  return 'Unknown';
};

/**
 * Sortable table header component
 */
interface SortableHeaderProps {
  column: SortConfig['column'];
  currentSort?: SortConfig;
  onSort: (column: SortConfig['column']) => void;
  children: React.ReactNode;
  className?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ 
  column, 
  currentSort, 
  onSort, 
  children,
  className = '' 
}) => {
  const isActive = currentSort?.column === column;
  const direction = currentSort?.direction || 'asc';

  return (
    <th 
      className={`
        px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider
        cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
        ${className}
      `}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <div className="flex flex-col">
          <ChevronUp 
            className={`h-3 w-3 ${
              isActive && direction === 'asc' 
                ? 'text-blue-600 dark:text-blue-400' 
                : 'text-gray-400'
            }`} 
          />
          <ChevronDown 
            className={`h-3 w-3 -mt-1 ${
              isActive && direction === 'desc' 
                ? 'text-blue-600 dark:text-blue-400' 
                : 'text-gray-400'
            }`} 
          />
        </div>
      </div>
    </th>
  );
};

/**
 * Document row component with Microsoft Graph metadata
 */
interface DocumentRowProps {
  document: EnhancedSPEDocument;
  onDocumentAction: (action: string, document: SPEDocument, event?: React.MouseEvent) => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({ document, onDocumentAction }) => {
  const handleAction = useCallback((action: string, event?: React.MouseEvent) => {
    onDocumentAction(action, document, event);
  }, [document, onDocumentAction]);

  return (
    <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
      {/* File Name */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <FileTypeIcon 
            fileName={document.name} 
            mimeType={document.mimeType}
            size="md"
            variant="icon"
          />
          <div className="flex-1 min-w-0">
            <button
              onClick={() => handleAction('preview')}
              className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left cursor-pointer"
              title="Click to preview"
            >
              {document.name}
            </button>
          </div>
        </div>
      </td>

      {/* File Size */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {document.formattedSize}
        </span>
      </td>

      {/* Created Date & User */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {formatDate(document.createdDateTime)}
            </div>
            {document.createdBy && (
              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                <User className="h-3 w-3" />
                <span>{getDisplayName(document.createdBy)}</span>
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Modified Date & User */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {formatDate(document.lastModifiedDateTime)}
            </div>
            {document.lastModifiedBy && (
              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                <User className="h-3 w-3" />
                <span>{getDisplayName(document.lastModifiedBy)}</span>
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end">
          <button
            onClick={(e) => handleAction('showContextMenu', e)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            title="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

/**
 * Enhanced DocumentList Component
 * 
 * Professional document table with Microsoft Graph API integration,
 * sortable columns, rich metadata display, and interactive actions.
 */
export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  isLoading = false,
  onDocumentAction,
  onSort,
  sortConfig,
  className = ''
}) => {
  // Enhance documents with computed metadata
  const enhancedDocuments = useMemo(() => {
    return documents.map((doc): EnhancedSPEDocument => {
      const category = getFileTypeCategory(doc.name, doc.mimeType);
      const extension = doc.name.toLowerCase().split('.').pop() || '';
      const canPreview = ['office', 'pdf', 'image', 'text'].includes(category);
      const isOfficeDocument = category === 'office' || category === 'spreadsheet' || category === 'presentation';
      
      return {
        ...doc,
        fileExtension: extension,
        formattedSize: formatFileSize(doc.size),
        fileTypeCategory: category,
        canPreview,
        isOfficeDocument
      };
    });
  }, [documents]);

  // Handle sorting
  const handleSort = useCallback((column: SortConfig['column']) => {
    if (!onSort) return;

    const direction = sortConfig?.column === column && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    onSort(column, direction);
  }, [onSort, sortConfig]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 shadow rounded-lg ${className}`}>
        <div className="px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading documents...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (enhancedDocuments.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-800 shadow rounded-lg ${className}`}>
        <div className="px-6 py-12 text-center">
          <FileTypeIcon fileName="empty.txt" size="lg" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            No documents found in this folder
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <SortableHeader 
                column="name" 
                currentSort={sortConfig} 
                onSort={handleSort}
                className="w-2/5"
              >
                Name
              </SortableHeader>
              
              <SortableHeader 
                column="size" 
                currentSort={sortConfig} 
                onSort={handleSort}
                className="w-1/8"
              >
                Size
              </SortableHeader>
              
              <SortableHeader 
                column="created" 
                currentSort={sortConfig} 
                onSort={handleSort}
                className="w-1/4"
              >
                Created
              </SortableHeader>
              
              <SortableHeader 
                column="modified" 
                currentSort={sortConfig} 
                onSort={handleSort}
                className="w-1/4"
              >
                Modified
              </SortableHeader>
              
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {enhancedDocuments.map((document) => (
              <DocumentRow
                key={document.id}
                document={document}
                onDocumentAction={onDocumentAction}
              />
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Document count footer */}
      <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-t border-gray-200 dark:border-gray-600">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {enhancedDocuments.length} document{enhancedDocuments.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default DocumentList;
